import { useState, useEffect, Fragment } from "react";
import Title from "../shared/components/Title";
import apiClient from "../utils/apiClient";
import DateTimePicker from "../shared/components/DateTimePicker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faDownload } from "@fortawesome/free-solid-svg-icons";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from "recharts";

const CORAL = "#F4736E";
const BLUE  = "#1a5691";
const NAVY  = "#144677";

const FUNNEL_COLORS = [NAVY, BLUE, "#2477b8", "#3b82f6", CORAL, "#c85b56"];

function fmtMonth(iso) {
  if (!iso) return "";
  const [y, m] = iso.split("-");
  return new Date(+y, +m - 1).toLocaleString("en-AU", { month: "short", year: "2-digit" });
}

function fmtDate(iso) {
  if (!iso) return "—";
  // Python str() on a Snowflake TIMESTAMP_NTZ gives "YYYY-MM-DD HH:MM:SS" (no tz marker).
  // Replace space with T and append Z so the browser parses it as UTC, then display in Melbourne local time.
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(iso)
    ? iso.replace(" ", "T") + "Z"
    : iso;
  return new Date(normalized).toLocaleDateString("en-AU", {
    day: "numeric", month: "short", year: "numeric",
    timeZone: "Australia/Melbourne",
  });
}

function Card({ children, className = "" }) {
  return <div className={`bg-white rounded-2xl shadow-sm ${className}`}>{children}</div>;
}

function SectionHeader({ title, sub }) {
  return (
    <div className="mb-5 pl-3" style={{ borderLeft: `3px solid ${CORAL}` }}>
      <h2 className="text-base font-bold text-gray-800">{title}</h2>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function KpiCard({ label, value, sub, accent }) {
  const color = accent || BLUE;
  return (
    <div className="bg-white rounded-2xl shadow-sm px-5 py-4" style={{ borderLeft: `4px solid ${color}` }}>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">{label}</p>
      <p className="text-3xl font-black" style={{ color }}>{value ?? "—"}</p>
      {sub && <p className="text-xs text-gray-400 mt-1.5">{sub}</p>}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    "Active":          { bg: "#dcfce7", text: "#16a34a" },
    "Dormant":         { bg: "#fef9c3", text: "#ca8a04" },
    "Inactive":        { bg: "#fee2e2", text: "#dc2626" },
    "Never Logged In": { bg: "#f3f4f6", text: "#6b7280" },
  };
  const c = map[status] || map["Never Logged In"];
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ background: c.bg, color: c.text }}>
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: c.text }} />
      {status}
    </span>
  );
}

function EngagementBar({ label, value, total, color }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="w-32 text-xs text-gray-500 text-right shrink-0">{label}</div>
      <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="w-20 text-xs font-bold tabular-nums" style={{ color }}>
        {value.toLocaleString()} <span className="font-normal text-gray-400">({pct}%)</span>
      </div>
    </div>
  );
}

function ChartTip({ active, payload, label, valueKey }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-xl shadow-xl text-xs overflow-hidden"
      style={{ border: `1px solid ${NAVY}20` }}>
      <div className="px-4 py-2" style={{ background: NAVY }}>
        <p className="font-bold text-white">{fmtMonth(label)}</p>
      </div>
      <div className="px-4 py-2.5">
        <p className="font-bold" style={{ color: CORAL }}>
          {payload[0].value.toLocaleString()} {valueKey}
        </p>
      </div>
    </div>
  );
}

const TH = ({ children, right }) => (
  <th className={`px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider whitespace-nowrap ${right ? "text-right" : "text-left"}`}>
    {children}
  </th>
);

export default function JupiterPortal() {
  const [summary,          setSummary]          = useState(null);
  const [consignmentTrend, setConsignmentTrend] = useState([]);
  const [queryTrend,       setQueryTrend]       = useState([]);
  const [engagement,       setEngagement]       = useState(null);
  const [funnel,           setFunnel]           = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [search,           setSearch]           = useState("");
  const [companyConsignments,        setCompanyConsignments]        = useState([])
  const [expandedConsignmentCompany, setExpandedConsignmentCompany] = useState(null)
  const [currentConsignmentDetails,  setCurrentConsignmentDetails]  = useState([])
  const [loadingConsignmentDetail,   setLoadingConsignmentDetail]   = useState(false)
  const [consignmentDateFrom,        setConsignmentDateFrom]        = useState(null)
  const [consignmentDateTo,          setConsignmentDateTo]          = useState(null)
  const [downloadingCompany,         setDownloadingCompany]         = useState(null)
  const [downloadingAll,             setDownloadingAll]             = useState(false)

  const fmtIso = (d) => {
    if (!d) return null;
    const y  = d.getFullYear();
    const m  = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dy}`;
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      apiClient.get("/api/v1/jupiterPortal/summary").then(r => r.json()),
      apiClient.get("/api/v1/jupiterPortal/consignment-trend").then(r => r.json()),
      apiClient.get("/api/v1/jupiterPortal/query-trend").then(r => r.json()),
      apiClient.get("/api/v1/jupiterPortal/engagement").then(r => r.json()),
      apiClient.get("/api/v1/jupiterPortal/adoption-funnel").then(r => r.json()),
    ]).then(([s, ct, qt, e, f]) => {
      setSummary(s);
      setConsignmentTrend(ct);
      setQueryTrend(qt);
      setEngagement(e);
      setFunnel(f);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (consignmentDateFrom) params.set("date_from", fmtIso(consignmentDateFrom));
    if (consignmentDateTo)   params.set("date_to",   fmtIso(consignmentDateTo));
    const qs = params.toString();
    apiClient
      .get(`/api/v1/jupiterPortal/companies-consignments${qs ? `?${qs}` : ""}`)
      .then(r => r.json())
      .then(cc => setCompanyConsignments(cc));
  }, [consignmentDateFrom, consignmentDateTo]);

  const toggleConsignmentCompany = (companyName) => {
    setExpandedConsignmentCompany(prev => prev === companyName ? null : companyName);
  };

  useEffect(() => {
    if (!expandedConsignmentCompany) {
      setCurrentConsignmentDetails([]);
      return;
    }
    setLoadingConsignmentDetail(true);
    const params = new URLSearchParams({ company: expandedConsignmentCompany });
    if (consignmentDateFrom) params.set("date_from", fmtIso(consignmentDateFrom));
    if (consignmentDateTo)   params.set("date_to",   fmtIso(consignmentDateTo));
    apiClient
      .get(`/api/v1/jupiterPortal/companies-consignment_detail?${params}`)
      .then(r => r.json())
      .then(rows => {
        setCurrentConsignmentDetails(rows);
        setLoadingConsignmentDetail(false);
      });
  }, [expandedConsignmentCompany, consignmentDateFrom, consignmentDateTo]);

  const filteredCompanyConsignments = companyConsignments.filter(c =>
    c.company.toLowerCase().includes(search.toLowerCase())
  );

  const downloadCSV = async () => {
    setDownloadingAll(true);
    try {
      const params = new URLSearchParams();
      if (consignmentDateFrom) params.set("date_from", fmtIso(consignmentDateFrom));
      if (consignmentDateTo)   params.set("date_to",   fmtIso(consignmentDateTo));
      const flat = await apiClient
        .get(`/api/v1/jupiterPortal/all-consignments-detail${params.toString() ? `?${params}` : ""}`)
        .then(r => r.json());
      const headers = ["Company","Consignment #","Customer Ref","From","To","Carrier Service","Tracking Status","Qty","Weight (kg)","Created"];
      const csvRows = flat.map(d => [
        d.customer_name || "",
        d.consignment_number,
        d.customer_reference || "",
        d.from_name || d.from_location_nk || "",
        d.to_name   || d.to_location_nk   || "",
        d.carrier_service_name || "",
        d.tracking_status || d.jupiter_consignment_status || "",
        d.total_quantity ?? "",
        d.total_weight ?? "",
        d.jupiter_created_at ? fmtDate(d.jupiter_created_at) : "",
      ].map(v => `"${String(v).replace(/"/g, '""')}"`));
      const csv  = [headers.join(","), ...csvRows.map(r => r.join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      const from = consignmentDateFrom ? fmtIso(consignmentDateFrom) : "all";
      const to   = consignmentDateTo   ? fmtIso(consignmentDateTo)   : "all";
      a.download = `jupiter-all-consignments-${from}-to-${to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingAll(false);
    }
  };
  const downloadCompanyCSV = async (companyName) => {
    setDownloadingCompany(companyName);
    try {
      let rows;
      if (expandedConsignmentCompany === companyName && currentConsignmentDetails.length > 0) {
        rows = currentConsignmentDetails;
      } else {
        const params = new URLSearchParams({ company: companyName });
        if (consignmentDateFrom) params.set("date_from", fmtIso(consignmentDateFrom));
        if (consignmentDateTo)   params.set("date_to",   fmtIso(consignmentDateTo));
        rows = await apiClient
          .get(`/api/v1/jupiterPortal/companies-consignment_detail?${params}`)
          .then(r => r.json());
      }
      const headers = ["Consignment #","Customer Ref","From","To","Carrier Service","Tracking Status","Qty","Weight (kg)","Created"];
      const csvRows = rows.map(d => [
        d.consignment_number,
        d.customer_reference || "",
        d.from_name || d.from_location_nk || "",
        d.to_name   || d.to_location_nk   || "",
        d.carrier_service_name || "",
        d.tracking_status || d.jupiter_consignment_status || "",
        d.total_quantity ?? "",
        d.total_weight ?? "",
        d.jupiter_created_at ? fmtDate(d.jupiter_created_at) : "",
      ].map(v => `"${String(v).replace(/"/g, '""')}"`));
      const csv  = [headers.join(","), ...csvRows.map(r => r.join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `${companyName.trim()}-consignments.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingCompany(null);
    }
  };

  const funnelMax = funnel[0]?.companies || 1;

  return (
    <div className="min-h-screen p-6 space-y-6" style={{ background: "#f0f5fb" }}>
      <Title title="Jupiter Ops Intelligence" />

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <KpiCard
          label="Companies"
          value={loading ? "…" : summary?.total_companies?.toLocaleString()}
          sub="registered on Jupiter"
          accent={NAVY}
        />
        <KpiCard
          label="Users"
          value={loading ? "…" : summary?.total_users?.toLocaleString()}
          sub="registered accounts"
          accent={BLUE}
        />
        <KpiCard
          label="Logged In"
          value={loading ? "…" : summary?.users_logged_in?.toLocaleString()}
          sub={`of ${summary?.total_users ?? "—"} ever active`}
          accent={BLUE}
        />
        <KpiCard
          label="Consignments"
          value={loading ? "…" : summary?.total_consignments?.toLocaleString()}
          sub="booked through Jupiter"
          accent={CORAL}
        />
        <KpiCard
          label="Queries Raised"
          value={loading ? "…" : summary?.total_queries?.toLocaleString()}
          sub="tickets via portal"
          accent={CORAL}
        />
      </div>

      {/* ── Trend Charts ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6">
          <SectionHeader
            title="Consignment Bookings by Month"
            sub="Consignments created through Jupiter"
          />
          {loading ? (
            <div className="h-52 flex items-center justify-center text-sm text-gray-400">Loading…</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={consignmentTrend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 11, fill: "#6b7280" }} />
                <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} allowDecimals={false} />
                <Tooltip content={<ChartTip valueKey="consignments" />} />
                <Bar dataKey="consignments" radius={[4, 4, 0, 0]} maxBarSize={48}>
                  {consignmentTrend.map((_, i) => (
                    <Cell key={i} fill={i === consignmentTrend.length - 1 ? CORAL : BLUE} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-6">
          <SectionHeader
            title="Query Volume by Month"
            sub="Support tickets raised through Jupiter"
          />
          {loading ? (
            <div className="h-52 flex items-center justify-center text-sm text-gray-400">Loading…</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={queryTrend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 11, fill: "#6b7280" }} />
                <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} allowDecimals={false} />
                <Tooltip content={<ChartTip valueKey="queries" />} />
                <Bar dataKey="queries" radius={[4, 4, 0, 0]} maxBarSize={48}>
                  {queryTrend.map((_, i) => (
                    <Cell key={i} fill={i === queryTrend.length - 1 ? CORAL : NAVY} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* ── Adoption Funnel + Engagement ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6">
          <SectionHeader
            title="Company Adoption Funnel"
            sub="How far companies progress through the platform"
          />
          {loading || !funnel.length ? (
            <div className="h-52 flex items-center justify-center text-sm text-gray-400">Loading…</div>
          ) : (
            <div className="space-y-3 pt-1">
              {funnel.map((f, i) => {
                const pct = Math.round((f.companies / funnelMax) * 100);
                const color = FUNNEL_COLORS[i] || BLUE;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-36 text-xs text-gray-500 text-right shrink-0">{f.stage}</div>
                    <div className="flex-1 h-7 bg-gray-100 rounded-lg overflow-hidden relative">
                      <div
                        className="h-full rounded-lg transition-all duration-700"
                        style={{ width: `${pct}%`, background: color }}
                      />
                      {pct > 12 && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-xs font-bold text-white pointer-events-none">
                          {f.companies}
                        </span>
                      )}
                    </div>
                    <div className="w-24 text-xs tabular-nums text-right">
                      <span className="font-bold" style={{ color }}>{f.companies}</span>
                      <span className="text-gray-400 ml-1">({pct}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="p-6">
          <SectionHeader
            title="User Engagement"
            sub="Login activity across all registered users"
          />
          {loading || !engagement ? (
            <div className="h-52 flex items-center justify-center text-sm text-gray-400">Loading…</div>
          ) : (
            <div className="space-y-5 pt-2">
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="rounded-xl px-4 py-3 text-center"
                  style={{ background: `${BLUE}10`, border: `1px solid ${BLUE}20` }}>
                  <p className="text-2xl font-black" style={{ color: BLUE }}>
                    {engagement.active_30d}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">Active last 30 days</p>
                </div>
                <div className="rounded-xl px-4 py-3 text-center"
                  style={{ background: `${NAVY}10`, border: `1px solid ${NAVY}20` }}>
                  <p className="text-2xl font-black" style={{ color: NAVY }}>
                    {engagement.active_90d}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">Active last 90 days</p>
                </div>
              </div>
              <EngagementBar label="Ever logged in"  value={engagement.ever_logged_in}  total={engagement.total_users} color={BLUE} />
              <EngagementBar label="Active (90 days)" value={engagement.active_90d}     total={engagement.total_users} color={NAVY} />
              <EngagementBar label="Active (30 days)" value={engagement.active_30d}     total={engagement.total_users} color={CORAL} />
              <EngagementBar label="Never logged in" value={engagement.never_logged_in} total={engagement.total_users} color="#d1d5db" />
            </div>
          )}
        </Card>
      </div>
            {/* ── Company Consignements ── */}
      <Card className="p-6">
        <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
          <div className="pl-3" style={{ borderLeft: `3px solid ${CORAL}` }}>
            <h2 className="text-base font-bold text-gray-800">Company Consignments</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {filteredCompanyConsignments.length} of {companyConsignments.length} companies — click a row to see individual consignments
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* download */}
            <button
              onClick={downloadCSV}
              disabled={companyConsignments.length === 0 || downloadingAll}
              className="flex items-center gap-1.5 rounded-2xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-blue-50 hover:border-blue-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {downloadingAll ? (
                <>
                  <span className="w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                  Downloading…
                </>
              ) : (
                <>↓ Download All</>
              )}
            </button>
            {/* date range pill */}
            <div className="flex items-center gap-1.5 rounded-2xl border border-gray-200 bg-gray-50 px-3 py-1.5">
              <span className="text-xs text-gray-400 font-medium whitespace-nowrap">From</span>
              <div className="-mb-4">
                <DateTimePicker
                  selected={consignmentDateFrom}
                  onChange={(date) => setConsignmentDateFrom(date)}
                  placeholder="Start date"
                  maxDate={new Date()}
                />
              </div>
              <span className="text-gray-300 text-xs">→</span>
              <span className="text-xs text-gray-400 font-medium whitespace-nowrap">To</span>
              <div className="-mb-4">
                <DateTimePicker
                  selected={consignmentDateTo}
                  onChange={(date) => setConsignmentDateTo(date)}
                  placeholder="End date"
                  maxDate={new Date()}
                />
              </div>
              {(consignmentDateFrom || consignmentDateTo) && (
                <button
                  onClick={() => {
                    setConsignmentDateFrom(null);
                    setConsignmentDateTo(null);
                  }}
                  className="ml-1 text-gray-300 hover:text-red-400 text-sm leading-none transition-colors"
                >✕</button>
              )}
            </div>
            {/* search */}
            <input
              type="text"
              placeholder="Search company…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="text-sm border border-gray-200 rounded-2xl bg-gray-50 px-3 py-2 outline-none focus:ring-2 w-48"
              style={{ "--tw-ring-color": `${NAVY}40` }}
            />
          </div>
        </div>

        {loading ? (
          <div className="h-32 flex items-center justify-center text-sm text-gray-400">Loading…</div>
        ) : (
          <div className="overflow-x-auto rounded-xl">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: NAVY }}>
                  <TH></TH>
                  <TH>Company</TH>
                  <TH right>Total Consignments</TH>
                  <TH></TH>
                </tr>
              </thead>
              <tbody>
                {filteredCompanyConsignments.map((c) => {
                  const isExpanded   = expandedConsignmentCompany === c.company;
                  const isLoadingDet = isExpanded && loadingConsignmentDetail;
                  const details      = isExpanded ? currentConsignmentDetails : [];
                  return (
                    <Fragment key={c.company}>
                      <tr
                        className={`border-b border-gray-50 cursor-pointer transition-colors ${
                          isExpanded ? "bg-blue-50/60" : "hover:bg-blue-50/40"
                        }`}
                        onClick={() => toggleConsignmentCompany(c.company)}
                      >
                        <td className="px-3 py-3 text-gray-400 w-8">
                          <FontAwesomeIcon
                            icon={isExpanded ? faChevronDown : faChevronRight}
                            className="w-3 h-3"
                          />
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-800">{c.company.trim()}</td>
                        <td className="px-4 py-3 text-right text-gray-500 text-xs">
                          {c.total_consignments}
                        </td>
                        <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                          <button
                            title={`Download ${c.company.trim()} consignments`}
                            onClick={() => downloadCompanyCSV(c.company)}
                            disabled={downloadingCompany === c.company}
                            className="flex items-center justify-center w-7 h-7 rounded-lg border border-gray-200 bg-white hover:bg-blue-50 hover:border-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            {downloadingCompany === c.company ? (
                              <span className="w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                            ) : (
                              <FontAwesomeIcon icon={faDownload} className="w-3 h-3 text-gray-500" />
                            )}
                          </button>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="bg-blue-50/20">
                          <td colSpan={4} className="px-10 py-4 border-b border-gray-100">
                            {isLoadingDet ? (
                              <p className="text-xs text-gray-400">Loading consignments…</p>
                            ) : details.length === 0 ? (
                              <p className="text-xs text-gray-400">No consignments found.</p>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="border-b border-gray-200">
                                      <th className="pb-2 text-left text-gray-400 font-semibold uppercase tracking-wider">Consignment #</th>
                                      <th className="pb-2 text-left text-gray-400 font-semibold uppercase tracking-wider pl-4">Customer Ref</th>
                                      <th className="pb-2 text-left text-gray-400 font-semibold uppercase tracking-wider pl-4">From</th>
                                      <th className="pb-2 text-left text-gray-400 font-semibold uppercase tracking-wider pl-4">To</th>
                                      <th className="pb-2 text-left text-gray-400 font-semibold uppercase tracking-wider pl-4">Carrier Service</th>
                                      <th className="pb-2 text-left text-gray-400 font-semibold uppercase tracking-wider pl-4">Tracking Status</th>
                                      <th className="pb-2 text-right text-gray-400 font-semibold uppercase tracking-wider">Qty</th>
                                      <th className="pb-2 text-right text-gray-400 font-semibold uppercase tracking-wider">Weight</th>
                                      <th className="pb-2 text-right text-gray-400 font-semibold uppercase tracking-wider">Created</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {details.map((d, j) => (
                                      <tr key={j} className="border-b border-gray-100 last:border-0">
                                        <td className="py-2 font-medium text-gray-700 whitespace-nowrap">{d.consignment_number}</td>
                                        <td className="py-2 pl-4 text-gray-500 whitespace-nowrap">{d.customer_reference || "—"}</td>
                                        <td className="py-2 pl-4 text-gray-500 whitespace-nowrap">{d.from_name || d.from_location_nk || "—"}</td>
                                        <td className="py-2 pl-4 text-gray-500 whitespace-nowrap">{d.to_name || d.to_location_nk || "—"}</td>
                                        <td className="py-2 pl-4 text-gray-500 whitespace-nowrap">{d.carrier_service_name || "—"}</td>
                                        <td className="py-2 pl-4 whitespace-nowrap">
                                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                                            style={{ background: `${BLUE}15`, color: BLUE }}>
                                            {d.tracking_status || d.jupiter_consignment_status || "—"}
                                          </span>
                                        </td>
                                        <td className="py-2 text-right tabular-nums text-gray-500">{d.total_quantity ?? "—"}</td>
                                        <td className="py-2 text-right tabular-nums text-gray-500">{d.total_weight != null ? `${d.total_weight} kg` : "—"}</td>
                                        <td className="py-2 text-right text-gray-400 whitespace-nowrap">{fmtDate(d.jupiter_created_at)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}

                {filteredCompanyConsignments.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-400 text-sm">
                      No companies match &quot;{search}&quot;
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

    </div>
  );
}
